import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useFormik } from "formik";
import { get, post, put } from "../../../../../helpers/api_helper";
import add_stock from "../../../../../Assets/svg/stockExchange/add_stock.svg";
import { stockExchangeSchema } from "../../../../../helpers/yup_validator";
import toast from "react-hot-toast";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import countryList from "react-select-country-list";
import Select from "react-select";
import { STOCK_ADD, STOCK_EDIT } from "../../../../../helpers/url_helper";
import { useGetAllPlansQuery } from "../../../../../store/services/utility.service";

const AddNewStockExchange = ({
  showModal,
  setShowModal,
  handleDataRefresh,
  mode,
  stockData,
  resetFormOnClose,
}) => {
  const [timezones, setTimezones] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedTimezone, setSelectedTimezone] = useState(null);

  // get all plans
  const { data: plans, isLoading } = useGetAllPlansQuery({ visibility: "custom" });

  const planOptions = useMemo(() => {
    if (!plans?.length) {
      return [];
    }

    return plans.map((plan) => ({
      label: `${plan.name}`,
      value: plan?._id
    }))
  }, [plans])

  useEffect(() => {
    const fetchTimezones = async () => {
      try {
        const response = await get("/user/admin/timezone/fetch");
        setTimezones(response?.data);
      } catch (error) {
        console.error("Failed to fetch timezones", error);
      }
    };

    fetchTimezones();
  }, []);

  const formik = useFormik({
    initialValues: {
      ticker_name: "",
      stock_name: "",
      location: "",
      timezone: "",
      opening_time: "",
      closing_time: "",
      visibility: "public",
      planIds: [],
      status: "active",
    },
    validationSchema: stockExchangeSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, setErrors, resetForm }) => {
      try {
        const payload = {
          ...values,
          planIds: values.planIds?.map(plan => plan.value)
        }

        if (mode === "create") {
          await post(STOCK_ADD, payload);
          toast.success("Stock exchange created successfully");
        } else if (mode === "edit") {
          await put(`${STOCK_EDIT}/${stockData._id}`, payload);
          toast.success("Stock exchange updated successfully");
        }
        setShowModal(false);
        handleDataRefresh();
        resetForm();
      } catch (error) {
        if (error) {
          setErrors({
            submissionError: error?.response?.data?.message
              ? error.response.data.message
              : error.response?.data?.error || "An error occurred.",
          });
          toast.error(
            error?.response?.data?.message
              ? error.response.data.message
              : error.response?.data?.error || "An error occurred."
          );
        } else {
          setErrors({
            submissionError: "An error occurred while submitting the form.",
          });
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (mode === "edit" && stockData) {
      const country = countryList()
        .getData()
        .find((c) => c?.label === stockData?.location);
      setSelectedCountry(country);
      const timezone = timezones.find(
        (tz) => tz?.name === stockData?.timezone?.name
      );
      setSelectedTimezone(
        timezone ? { value: timezone._id, label: timezone.name } : null
      );
      const selectedPlans = planOptions?.filter(plan => stockData?.planIds?.includes(plan.value));
      const selectedPlanOptions = selectedPlans.map(plan => ({
        label: plan.label,
        value: plan.value
      }))

      formik.setValues({
        ticker_name: stockData?.ticker_name,
        stock_name: stockData?.stock_name,
        location: stockData?.location,
        timezone: stockData?.timezone?._id,
        opening_time: stockData?.opening_time,
        closing_time: stockData?.closing_time,
        visibility: stockData?.visibility,
        planIds: selectedPlanOptions?.length ? selectedPlanOptions : [],
        status: stockData?.status,
      });
    } else if (mode === "create" || resetFormOnClose) {
      formik.resetForm();
      setSelectedCountry(null);
      setSelectedTimezone(null);
    }
  }, [mode, stockData, resetFormOnClose, timezones, planOptions]);

  const handleCountryChange = (value) => {
    setSelectedCountry(value);
    formik.setFieldValue("location", value ? value?.label : "");
  };

  const handleTimezoneChange = (value) => {
    setSelectedTimezone(value);
    formik.setFieldValue("timezone", value ? value?.value : "");
  };

  const countryOptions = useMemo(() => countryList().getData(), []);
  const timezoneOptions = useMemo(() => {
    return timezones.map((timezone) => ({
      value: timezone?._id,
      label: timezone?.name,
    }));
  }, [timezones]);

  return (
    <Modal
      show={showModal}
      size="md"
      onHide={() => setShowModal(false)}
      backdrop="static"
      keyboard={false}
      centered
      scrollable="true"
    >
      <Modal.Body>
        <div className="topHeader d-flex align-items-start justify-content-between gap-10 pb-2">
          <div className="left">
            <h5 className="m-0 fw-sbold">
              {mode === "create" ? "Add New Stock" : "Edit Stock"}
            </h5>
            <p className="m-0 text-muted">
              {mode === "create"
                ? "Add a new stock exchange to the list."
                : "Edit the details of the stock exchange."}
            </p>
          </div>
          <Button
            className="p-0 border-0"
            variant="transparent"
            onClick={() => setShowModal(false)}
          >
            <img src={add_stock} alt="" />
          </Button>
        </div>
        <div className="FormWrapper">
          {formik.errors.submissionError && (
            <div className="alert alert-danger">
              {formik.errors.submissionError}
            </div>
          )}
          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col className="my-2" lg="12">
                <label
                  htmlFor="ticker_name"
                  className="form-label text-dark m-0 pb-1"
                >
                  Ticker Name
                </label>
                <input
                  type="text"
                  id="ticker_name"
                  name="ticker_name"
                  value={formik.values.ticker_name}
                  placeholder="Ticker Name"
                  className="form-control"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.ticker_name && formik.errors.ticker_name ? (
                  <div className="text-danger">{formik.errors.ticker_name}</div>
                ) : null}
              </Col>
              <Col className="my-2" lg="12">
                <label
                  htmlFor="stock_name"
                  className="form-label text-dark m-0 pb-1"
                >
                  Stock Exchange Name
                </label>
                <input
                  type="text"
                  id="stock_name"
                  name="stock_name"
                  value={formik.values?.stock_name}
                  placeholder="Stock Exchange Name"
                  className="form-control"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.stock_name && formik.errors.stock_name ? (
                  <div className="text-danger">{formik.errors.stock_name}</div>
                ) : null}
              </Col>
              <Col className="my-2" lg="12">
                <label
                  htmlFor="location"
                  className="form-label text-dark m-0 pb-1"
                >
                  Country Name
                </label>
                <Select
                  id="location"
                  name="location"
                  value={selectedCountry}
                  options={countryOptions}
                  onChange={handleCountryChange}
                  placeholder="Select a country"
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
                {formik.touched.location && formik.errors.location ? (
                  <div className="text-danger">{formik.errors.location}</div>
                ) : null}
              </Col>
              <Col className="my-2" lg="12">
                <label
                  htmlFor="timezone"
                  className="form-label text-dark m-0 pb-1"
                >
                  Timezone
                </label>
                <Select
                  id="timezone"
                  name="timezone"
                  value={selectedTimezone}
                  options={timezoneOptions}
                  onChange={handleTimezoneChange}
                  placeholder="Select a timezone"
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
                {formik.touched.timezone && formik.errors.timezone ? (
                  <div className="text-danger">{formik.errors.timezone}</div>
                ) : null}
              </Col>
              <Col className="my-2" md="6">
                <label
                  htmlFor="opening_time"
                  className="form-label text-dark m-0 pb-1"
                >
                  Opening Time
                </label>
                <DatePicker
                  id="opening_time"
                  name="opening_time"
                  selected={
                    formik.values.opening_time
                      ? new Date(formik.values.opening_time)
                      : null
                  }
                  onChange={(time) =>
                    formik.setFieldValue("opening_time", time)
                  }
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="h:mm aa"
                  className="form-control"
                  onBlur={formik.handleBlur}
                />
                {formik.touched.opening_time && formik.errors.opening_time ? (
                  <div className="text-danger">
                    {formik.errors.opening_time}
                  </div>
                ) : null}
              </Col>
              <Col className="my-2" md="6">
                <label
                  htmlFor="closing_time"
                  className="form-label text-dark m-0 pb-1"
                >
                  Closing Time
                </label>
                <DatePicker
                  id="closing_time"
                  name="closing_time"
                  selected={
                    formik.values.closing_time
                      ? new Date(formik.values.closing_time)
                      : null
                  }
                  onChange={(time) =>
                    formik.setFieldValue("closing_time", time)
                  }
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="h:mm aa"
                  className="form-control"
                  onBlur={formik.handleBlur}
                />
                {formik.touched.closing_time && formik.errors.closing_time ? (
                  <div className="text-danger">
                    {formik.errors.closing_time}
                  </div>
                ) : null}
              </Col>
              <Form.Group>
                <Form.Label>Visibility</Form.Label>
                <div className="d-flex gap-10 align-items-center">
                  <Form.Group className="py-2" controlId="planVisiblityPublic">
                    <Form.Check
                      type="radio"
                      label="Public"
                      name="visibility"
                      value={"public"}
                      checked={formik.values.visibility === "public"}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={formik.touched.visibility && formik.errors.visibility}
                    />
                  </Form.Group>
                  <Form.Group className="py-2" controlId="planVisiblityCustom">
                    <Form.Check
                      type="radio"
                      label="Custom"
                      name="visibility"
                      value={"custom"}
                      checked={formik.values.visibility === "custom"}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={formik.touched.visibility && formik.errors.visibility}
                    />
                  </Form.Group>
                </div>
                {formik.touched.visibility && formik.errors.visibility ? (
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.visibility}
                  </Form.Control.Feedback>
                ) : null}
              </Form.Group>
              {formik.values.visibility === "custom" && (
                <Form.Group className="py-2" controlId="planStatus">
                  <Form.Label>Select a plan to assign the stock</Form.Label>
                  <Select
                    id="plans"
                    value={formik.values.planIds}
                    options={planOptions}
                    onChange={(selected) =>
                      formik.setFieldValue("planIds", selected ? selected : [])
                    }
                    isClearable={true}
                    isDisabled={formik.isSubmitting}
                    maxMenuHeight={150}
                    isMulti={true}
                  />
                </Form.Group>
              )}
              <Col lg="12" className="my-2">
                <div className="d-flex align-items-center justify-content-between gap-10">
                  <label
                    htmlFor="status"
                    className="form-label text-dark fw-sbold m-0"
                  >
                    Status
                  </label>
                  <Form.Check
                    type="switch"
                    id="status"
                    name="status"
                    checked={formik.values.status === "active"}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "status",
                        e.target.checked ? "active" : "inactive"
                      )
                    }
                  />
                </div>
              </Col>
              <Col lg="12" className="my-2">
                <Col lg="12" className="my-2">
                  <Button
                    className="d-flex align-items-center w-100 justify-content-center commonBtn"
                    type="submit"
                    disabled={formik.isSubmitting}
                  >
                    {mode === "create"
                      ? "Add Stock Exchange"
                      : "Update Stock Exchange"}
                  </Button>
                </Col>
              </Col>
            </Row>
          </Form>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AddNewStockExchange;
